// App.js
import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { LanguageProvider, useLang } from './i18n/LanguageContext';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ThemeProvider, useTheme } from './theme/ThemeContext';
import SettingsPage from './pages/SettingsPage';
import GeneratePage from './pages/GeneratePage';
import DressGeneratePage from './pages/DressGeneratePage';
import ExplorerPage from './pages/ExplorerPage';
import GalleryPage from './pages/GalleryPage';
import FieldOptionsPage from './pages/FieldOptionsPage';
import AdminUsersPage from './pages/AdminUsersPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? children : <Navigate to="/login" replace />;
}

function RequireAdmin({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin ? children : <Navigate to="/" replace />;
}

function AppContent() {
  const { lang, toggleLang, t, fontFamily, fontWeight, fontSize } = useLang();
  const { user, isAdmin, logout } = useAuth();
  const { darkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  // Close mobile menu on route change
  React.useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  if (['/login', '/register'].includes(location.pathname)) {
    return (
      <div style={{ fontFamily, fontWeight, fontSize }}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
        </Routes>
      </div>
    );
  }

  // Exact links and icons to match the screenshot
  const sidebarLinks = [
    { to: '/explorer',       icon: 'explore',           label: t.navExplorer,        show: true },
    { to: '/dress-generate', icon: 'checkroom',         label: t.navDressGenerate,   show: !!user },
    { to: '/generate',       icon: 'magic_button',      label: t.navGenerate,        show: !!user },
    { to: '/gallery',        icon: 'gallery_thumbnail', label: t.navGallery,         show: !!user },
    { to: '/field-options',  icon: 'tune',              label: t.navFieldOptions,    show: isAdmin },
    { to: '/admin-users',    icon: 'group',             label: t.navAdminUsers,      show: isAdmin },
    { to: '/settings',       icon: 'settings',          label: t.navSettings,        show: isAdmin },
  ].filter(l => l.show);

  const mobileNavLinks = sidebarLinks.slice(0, 5);

  const isPremium = user && (user.subscriptionStatus === 'Premium' || user.subscriptionStatus === 'Premium+');
  const userInitial = user?.name ? user.name.charAt(0).toUpperCase() : 'A';
  const userName = user?.name || 'Administrator';

  return (
    <div className="app-shell" style={{ fontFamily, fontWeight, fontSize }}>

      {/* ── Mobile Top Header (hidden on desktop via CSS) ── */}
      <header className="mobile-top-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className="material-symbols-outlined" style={{ color: '#FA6868', fontSize: 24 }}>checkroom</span>
          <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#FA6868' }}>Atelier AI</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button className="mobile-header-btn lang-toggle-btn" onClick={toggleLang} title={lang === 'en' ? 'عربي' : 'English'}>
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>{lang === 'en' ? 'AR' : 'EN'}</span>
          </button>
          <button className="mobile-header-btn" onClick={toggleTheme}>
            <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
              {darkMode ? 'light_mode' : 'dark_mode'}
            </span>
          </button>
          <button className="mobile-header-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span className="material-symbols-outlined" style={{ fontSize: 24 }}>
              {mobileMenuOpen ? 'close' : 'menu'}
            </span>
          </button>
        </div>
      </header>

      {/* ── Mobile Slide Menu (hidden on desktop via CSS) ── */}
      {mobileMenuOpen && (
        <>
          <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
          <div className="mobile-slide-menu">
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 16px', borderBottom: '1px solid var(--border-light)' }}>
                <div className="avatar" style={{ width: 40, height: 40, minWidth: 40, fontSize: 16 }}>{userInitial}</div>
                <div>
                  <div className="user-name" style={{ fontSize: '0.9rem' }}>
                    {userName}
                    {isPremium && <span className="material-symbols-outlined premium-icon">crown</span>}
                  </div>
                  <div className="user-email">{user.email || 'admin@x.com'}</div>
                </div>
              </div>
            )}
            <ul style={{ listStyle: 'none', padding: '8px 0', flexGrow: 1 }}>
              {sidebarLinks.map(({ to, icon, label }) => {
                const isActive = location.pathname === to || (to === '/explorer' && location.pathname === '/');
                return (
                  <li key={to} style={{ padding: '0 8px' }}>
                    <NavLink to={to} className={`mobile-menu-link ${isActive ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 20 }}>{icon}</span>
                      {label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
            <div style={{ padding: '12px 8px', borderTop: '1px solid var(--border-subtle)' }}>
              <button className="mobile-menu-link" onClick={toggleLang} style={{ width: '100%' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 20 }}>translate</span>
                {lang === 'en' ? 'عربي — Arabic' : 'English — إنجليزي'}
              </button>
              {user ? (
                <button className="mobile-menu-link" onClick={handleLogout} style={{ color: 'var(--primary-color)', width: '100%' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>logout</span>
                  {t.navLogout}
                </button>
              ) : (
                <NavLink to="/login" className="mobile-menu-link" onClick={() => setMobileMenuOpen(false)} style={{ color: 'var(--primary-color)' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 20 }}>login</span>
                  {t.navLogin}
                </NavLink>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Desktop Sidebar (unchanged) ── */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
        <button className="toggle-btn" onClick={() => setCollapsed(!collapsed)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {collapsed ? 'chevron_right' : 'chevron_left'}
          </span>
        </button>

        <div className="sidebar-header">
          <div className="logo-area">
            <span className="material-symbols-outlined" style={{ color: '#FA6868' }}>checkroom</span>
            <span className="logo-text" style={{ color: '#FA6868' }}>Atelier AI</span>
          </div>
        </div>

        {user && (
          <div className="user-profile">
            <div className="avatar-wrapper">
              <div className="avatar">{userInitial}</div>
            </div>
            <div className="user-info">
              <div className="user-name">
                {userName}
                {isPremium && <span className="material-symbols-outlined premium-icon">crown</span>}
              </div>
              <div className="user-email">{user.email || 'admin@x.com'}</div>
            </div>
          </div>
        )}

        <ul className="nav-menu">
          {sidebarLinks.map(({ to, icon, label }) => {
            const isActive = location.pathname === to || (to === '/explorer' && location.pathname === '/');
            return (
              <li key={to} className={isActive ? 'active' : ''}>
                <NavLink to={to}>
                  <span className="material-symbols-outlined">{icon}</span>
                  <span className="nav-text">{label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>

        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleLang}>
            <span className="material-symbols-outlined">
              translate
            </span>
            <span className="nav-text">{lang === 'en' ? 'عربي' : 'English'}</span>
          </button>
          <button className="theme-toggle" onClick={toggleTheme}>
            <span className="material-symbols-outlined">
              {darkMode ? 'light_mode' : 'dark_mode'}
            </span>
            <span className="nav-text">{darkMode ? t.navLightMode : t.navDarkMode}</span>
          </button>
          {user ? (
            <button className="btn-logout" onClick={handleLogout}>
              <span className="material-symbols-outlined">logout</span>
              <span className="nav-text text">{t.navLogout}</span>
            </button>
          ) : (
            <NavLink to="/login" className="btn-logout" style={{ background: '#FA6868', color: '#fff', borderColor: '#FA6868' }}>
              <span className="material-symbols-outlined">login</span>
              <span className="nav-text text">{t.navLogin}</span>
            </NavLink>
          )}
        </div>
      </aside>

      <main className="main-content">
        <Routes>
          <Route path="/" element={<ExplorerPage />} />
          <Route path="/dress-generate" element={<RequireAuth><DressGeneratePage /></RequireAuth>} />
          <Route path="/generate" element={<RequireAuth><GeneratePage /></RequireAuth>} />
          <Route path="/gallery" element={<RequireAuth><GalleryPage /></RequireAuth>} />
          <Route path="/explorer" element={<ExplorerPage />} />
          <Route path="/settings" element={<RequireAdmin><SettingsPage /></RequireAdmin>} />
          <Route path="/field-options" element={<RequireAdmin><FieldOptionsPage /></RequireAdmin>} />
          <Route path="/admin-users" element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* ── Mobile Bottom Nav (hidden on desktop via CSS) ── */}
      <nav className="mobile-bottom-nav">
        {mobileNavLinks.map(({ to, icon, label }) => {
          const isActive = location.pathname === to || (to === '/explorer' && location.pathname === '/');
          return (
            <NavLink key={to} to={to} className={`mobile-nav-item ${isActive ? 'active' : ''}`}>
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>{icon}</span>
              <span>{label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;